/**
 * "Details (n)": everything about a case that is not the mail, the draft or the action,
 * in a side sheet (a Drawer from the right on the page, from the bottom in the Outlook
 * pane) with one Accordion section per subject. The sheet opens closed on every case;
 * the sections a person opened are remembered in this browser
 * (`i2q.case.sheet.sections`). `n` counts the rules that hold the case and the open
 * questions of the reply draft.
 *
 * Sections, by fixed id: checks (the rules, a blocked check of the quotation preview,
 * the price approval, and "Try again" when I2Q could not read the mail), customer (the
 * card, the matching review, the CRM machines), quotation (the quotation in the reply
 * text, its flags, claims and open questions), order-system (the quotation preview for
 * F&O, with "Quoted in F&O" on top), documents (attachments read, photos), related (the
 * thread, the quotations in F&O), history (what happened, earlier decisions, the sales
 * reply, the header defaults), feedback. "Quoted in F&O" (what sales quoted, 20 Oct 2026)
 * moves to the quotation section, else to related, when the order-system section is not
 * shown; the section ids are a usage enum, so it gets no section of its own.
 *
 * `CaseSheetSections` is the content on its own, so a test can render it without the
 * drawer. Read only apart from the writes the mounted cards already own.
 */

import { Icon, PanelRight } from "@norican/icons";
import { Accordion, Alert, Button, Drawer, Typography } from "@norican/react";
import { useState } from "react";

import { DraftDetails, QuotationPreview } from "@/features/case-review/answer-draft-panel";
import { AttachmentsReadCard } from "@/features/case-review/case-documents";
import { EarlierDecisionsCard, HistoryCard } from "@/features/case-review/case-history";
import { QuotationsCard, RelatedMailsCard } from "@/features/case-review/case-links";
import { CrmMachinesCard } from "@/features/case-review/crm-machines-panel";
import { CustomerCard } from "@/features/case-review/customer-card";
import { FeedbackControl } from "@/features/case-review/feedback-control";
import { FoDraftPanel } from "@/features/case-review/fo-draft-panel";
import { effectiveItem } from "@/features/case-review/line-semantics";
import { LiveResolve } from "@/features/case-review/live-resolve";
import { MatchingReview } from "@/features/case-review/matching-evidence";
import { OutcomePanel } from "@/features/case-review/outcome-panel";
import { PhotoPanel } from "@/features/case-review/photo-panel";
import { QuotationApprovalSection } from "@/features/case-review/quotation-approval";
import { CustomerHeaderCard } from "@/features/case-review/quote-history";
import { QuotedInFo } from "@/features/case-review/quoted-in-fo";
import { RuleFlagsPanel } from "@/features/case-review/rule-flags-panel";
import { type AnswerDraft, type FoDraftPreview } from "@/lib/api";
import { isOutOfScopeCase } from "@/lib/intents";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import { readRememberedList, rememberList } from "@/lib/remembered";
import { ACTION_LABELS, PRICE_APPROVAL_WORDS } from "@/lib/sales-flow";
import { track } from "@/lib/usage";

/** The section ids, in the order they are listed. */
export const SHEET_SECTIONS = [
  "checks",
  "customer",
  "quotation",
  "order-system",
  "documents",
  "related",
  "history",
  "feedback",
] as const;

export type SheetSection = (typeof SHEET_SECTIONS)[number];

export const SHEET_SECTION_LABELS: Record<SheetSection, string> = {
  checks: "Checks",
  customer: "Customer",
  quotation: "Quotation in the reply",
  "order-system": "Order system",
  documents: "Documents",
  related: "Related mails",
  history: "History",
  feedback: "Feedback",
};

/** The browser key of the opened sections. */
export const SHEET_SECTIONS_KEY = "i2q.case.sheet.sections";

/** The remembered open sections, only known ids. */
export function readSheetSections(): SheetSection[] {
  return readRememberedList(SHEET_SECTIONS_KEY, []).filter((id): id is SheetSection =>
    (SHEET_SECTIONS as readonly string[]).includes(id),
  );
}

/** The quotation "SQ0069260 (sent)" that already answers a closed case; null otherwise. */
export function alreadyQuotedOf(detail: QuoteCaseDetail): string | null {
  const answered =
    detail.workStatus === "replied" ||
    detail.workStatus === "done" ||
    detail.workStatus === "auto_closed" ||
    detail.state === "auto-closed";
  if (!answered) {
    return null;
  }
  const sent = (detail.quotations ?? []).find(
    (quotation) => quotation.status === "sent" || quotation.status === "confirmed",
  );
  if (sent) {
    return `${sent.number} (${sent.status})`;
  }
  const doc = detail.outcome?.doc_number?.trim();
  return doc && detail.outcome?.doc_type === "quotation" ? `${doc} (sent)` : null;
}

/** The currency of the priced lines when they agree on one; null otherwise. */
function pricedCurrencyOf(lines: readonly QuoteLine[]): string | null {
  const codes = new Set(
    lines
      .map((line) => line.price?.currency?.trim())
      .filter((code): code is string => !!code && code !== ""),
  );
  return codes.size === 1 ? ([...codes][0] ?? null) : null;
}

function hasWork(detail: QuoteCaseDetail): boolean {
  return detail.work != null || detail.workStatus != null;
}

export interface CaseSheetSectionsProps {
  detail: QuoteCaseDetail;
  /** The newest reply draft, for the quotation section. */
  draft: AnswerDraft | null;
  /** The cached quotation preview, for the blocked checks and the approval. */
  foDraft: FoDraftPreview | null;
  /** The open sections (controlled). */
  openSections: readonly SheetSection[];
  onOpenSectionsChange: (sections: SheetSection[]) => void;
  /** Reads the quotation preview again ("Check again" on a preview made before the last change). */
  onCheckAgain?: () => void;
  /**
   * Approves the held price of the line with this key: the working "Approve price" of a
   * held reason, for a viewer who may approve prices. Without it the button is not shown.
   */
  onApprovePrice?: (lineKey: string) => void;
}

/**
 * True when the cached quotation preview was built from the case as it is now: its
 * `work_version` is the version of the work row on the page. A preview without the
 * field (an older server) cannot be told apart and counts as current.
 */
export function previewIsCurrent(
  preview: Pick<FoDraftPreview, "work_version"> | null | undefined,
  detail: Pick<QuoteCaseDetail, "work">,
): boolean {
  if (!preview || typeof preview.work_version !== "number") {
    return true;
  }
  return preview.work_version === (detail.work?.version ?? 0);
}

/** States of a case for which no quotation is made, whatever its intent says. */
const NO_QUOTATION_STATES: readonly string[] = ["not-an-rfq", "duplicate"];

/** "Line 2" for the reason of a line, by its key; null for a line not shown. */
function lineLabelOf(detail: QuoteCaseDetail): (lineKey: string) => string | null {
  const shown = detail.lines.filter((line) => !line.fallback);
  return (lineKey) => {
    const index = shown.findIndex((line) => line.lineKey === lineKey);
    return index >= 0 ? `Line ${index + 1}` : null;
  };
}

/** The Accordion with every section that has content. */
export function CaseSheetSections({
  detail,
  draft,
  foDraft,
  openSections,
  onOpenSectionsChange,
  onCheckAgain,
  onApprovePrice,
}: CaseSheetSectionsProps) {
  const outOfScope = isOutOfScopeCase(detail);
  const lineLabel = lineLabelOf(detail);
  const realLines = detail.lines.filter((line) => !line.fallback);
  // A preview made before the last change of the case is not shown: its checks and its
  // approvals may be the ones of yesterday (ease round, 13 Oct 2026).
  const previewCurrent = previewIsCurrent(foDraft, detail);
  const preview = previewCurrent ? foDraft : null;
  const blockedChecks = (preview?.checks ?? []).filter((check) => check.status === "blocked");
  // No quotation is made for a mail that is not an inquiry, nor for a copy of another
  // case: the entity rules' notes on its header are noise there (bug hunt Q-29).
  const rules =
    outOfScope || NO_QUOTATION_STATES.includes(detail.state) ? null : (detail.rules ?? null);
  const rulesCount = rules?.reasons?.length ?? 0;
  const attachmentsRead = detail.attachmentsRead ?? [];
  const related = detail.related ?? [];
  const quotations = detail.quotations ?? [];
  const earlierDecisions = detail.earlierDecisions ?? [];
  const caseAccount = detail.work?.customer_account ?? detail.customerAccount ?? null;

  const sections: { id: SheetSection; content: React.ReactNode }[] = [];
  if (
    rulesCount > 0 ||
    blockedChecks.length > 0 ||
    foDraft ||
    (detail.engineError && detail.area)
  ) {
    // (An outdated preview keeps the section: it says so and offers "Check again".)
    sections.push({
      id: "checks",
      content: (
        <div className="flex flex-col gap-3">
          <RuleFlagsPanel
            rules={rules}
            entity={detail.area}
            lineLabel={lineLabel}
            onApprovePrice={onApprovePrice}
          />
          {blockedChecks.length > 0 && (
            <Alert tone="warning" title="Blocked in the quotation preview">
              <ul className="flex flex-col gap-0.5">
                {blockedChecks.map((check) => (
                  <li key={check.code} className="break-words">
                    {check.label}
                    {check.detail ? `. ${check.detail}` : ""}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          {foDraft && !previewCurrent && (
            <div className="flex flex-wrap items-center gap-2" data-preview-outdated>
              <Typography variant="caption" tone="muted">
                {PRICE_APPROVAL_WORDS.madeBefore}
              </Typography>
              {onCheckAgain && (
                <Button size="sm" variant="outline" tone="neutral" onClick={onCheckAgain}>
                  Check again
                </Button>
              )}
            </div>
          )}
          {preview && (
            <QuotationApprovalSection
              caseId={detail.id}
              preview={preview}
              lineIndexOf={(lineKey) => {
                const index = detail.lines.findIndex((l) => l.lineKey === lineKey);
                return index >= 0 ? index : undefined;
              }}
            />
          )}
          {detail.engineError && detail.area && (
            <LiveResolve area={detail.area} initialLine={realLines[0]?.enquiryText ?? ""} />
          )}
        </div>
      ),
    });
  }
  if (!outOfScope && (detail.customerBlock || detail.matchingReview)) {
    sections.push({
      id: "customer",
      content: (
        <div className="flex flex-col gap-3">
          <CustomerCard detail={detail} />
          <MatchingReview review={detail.matchingReview} />
          <CrmMachinesCard caseId={detail.id} />
        </div>
      ),
    });
  }
  // "Quoted in F&O": where a sales person looks for the F&O quotation (review F7).
  const groundTruth = detail.groundTruth ?? null;
  const showsOrderSystem = !outOfScope && hasWork(detail);
  const quotedIn: SheetSection | null = !groundTruth
    ? null
    : showsOrderSystem
      ? "order-system"
      : draft
        ? "quotation"
        : "related";
  const quotedBlock = <QuotedInFo truth={groundTruth} />;
  if (draft) {
    sections.push({
      id: "quotation",
      content: (
        <div className="flex flex-col gap-3">
          {quotedIn === "quotation" && quotedBlock}
          <QuotationPreview quotation={draft.quotation} />
          <DraftDetails draft={draft} />
        </div>
      ),
    });
  }
  if (showsOrderSystem) {
    sections.push({
      id: "order-system",
      content: (
        <div className="flex flex-col gap-3">
          {quotedIn === "order-system" && quotedBlock}
          <FoDraftPanel
            caseId={detail.id}
            lineIndexOf={(lineKey) => {
              const index = detail.lines.findIndex((l) => l.lineKey === lineKey);
              return index >= 0 ? index : undefined;
            }}
            context={{
              lineLabel,
              itemOf: (lineKey) => {
                const line = detail.lines.find((l) => l.lineKey === lineKey);
                return line
                  ? (effectiveItem(line).item ?? line.suggestions?.[0]?.item ?? null)
                  : null;
              },
              pricedCurrency: pricedCurrencyOf(realLines),
              mailIncoterms: detail.incoterms,
            }}
            alreadyQuoted={alreadyQuotedOf(detail)}
          />
        </div>
      ),
    });
  }
  if (attachmentsRead.length > 0) {
    sections.push({
      id: "documents",
      content: (
        <div className="flex flex-col gap-3">
          <AttachmentsReadCard caseId={detail.id} attachments={attachmentsRead} />
          <PhotoPanel caseId={detail.id} attachments={attachmentsRead} lines={detail.lines} />
        </div>
      ),
    });
  }
  if (related.length > 0 || quotations.length > 0 || quotedIn === "related") {
    sections.push({
      id: "related",
      content: (
        <div className="flex flex-col gap-3">
          {quotedIn === "related" && quotedBlock}
          <RelatedMailsCard related={related} />
          <QuotationsCard quotations={quotations} caseAccount={detail.customerAccount} />
        </div>
      ),
    });
  }
  if (
    hasWork(detail) ||
    earlierDecisions.length > 0 ||
    detail.olderResult ||
    detail.outcome ||
    (detail.customerHeader ?? []).length > 0
  ) {
    sections.push({
      id: "history",
      content: (
        <div className="flex flex-col gap-3">
          {detail.olderResult && <Alert tone="info">{detail.olderResult}</Alert>}
          {detail.outcome && <OutcomePanel outcome={detail.outcome} caseAccount={caseAccount} />}
          <CustomerHeaderCard rows={detail.customerHeader} />
          <EarlierDecisionsCard decisions={earlierDecisions} />
          {hasWork(detail) && <HistoryCard caseId={detail.id} />}
        </div>
      ),
    });
  }
  if (!outOfScope) {
    sections.push({
      id: "feedback",
      content: <FeedbackControl caseId={detail.id} lineIndex={null} />,
    });
  }

  return (
    <Accordion.Root
      value={openSections.filter((id) => sections.some((section) => section.id === id))}
      onValueChange={(value) => {
        const ids = (Array.isArray(value) ? value : []).filter((id): id is SheetSection =>
          (SHEET_SECTIONS as readonly string[]).includes(String(id)),
        );
        onOpenSectionsChange(ids);
      }}
      data-case-sheet-sections
    >
      {sections.map((section) => (
        <Accordion.Item key={section.id} value={section.id} data-sheet-section={section.id}>
          <Accordion.Header>
            <Accordion.Trigger>{SHEET_SECTION_LABELS[section.id]}</Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Panel>{section.content}</Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}

export interface CaseSheetProps extends Omit<
  CaseSheetSectionsProps,
  "openSections" | "onOpenSectionsChange"
> {
  variant: "page" | "pane";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The section to open on top of the remembered ones (the "Check:" action). */
  section: SheetSection | null;
}

/** The ghost "Details (n)" button under the action bar. */
export function DetailsButton({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      tone="neutral"
      startIcon={<Icon icon={PanelRight} size="xs" />}
      onClick={onClick}
      data-details-count={count}
    >
      {ACTION_LABELS.details(count)}
    </Button>
  );
}

/** The sheet itself: a right drawer on the page, a bottom drawer in the pane. */
export function CaseSheet({
  detail,
  draft,
  foDraft,
  variant,
  open,
  onOpenChange,
  section,
  onCheckAgain,
  onApprovePrice,
}: CaseSheetProps) {
  // The remembered sections, plus the one the action asked for; a change is remembered.
  const [openSections, setOpenSections] = useState<SheetSection[]>(readSheetSections);
  const [askedFor, setAskedFor] = useState<SheetSection | null>(null);
  if (section !== askedFor) {
    setAskedFor(section);
    if (section && !openSections.includes(section)) {
      setOpenSections([section, ...openSections]);
    }
  }
  return (
    <Drawer.Root
      open={open}
      side={variant === "pane" ? "bottom" : "right"}
      onOpenChange={(next) => {
        logger.debug("[case] details sheet", { case: detail.id, open: next });
        onOpenChange(next);
      }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup className={variant === "pane" ? "max-h-[85vh]" : "w-[min(100vw,36rem)]"}>
            <Drawer.Header>
              <Drawer.Title>
                <Typography as="span" variant="heading-6" className="text-fg">
                  Details
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body className="overflow-y-auto">
              <CaseSheetSections
                detail={detail}
                draft={draft}
                foDraft={foDraft}
                openSections={openSections}
                onOpenSectionsChange={(ids) => {
                  // The usage log: each section a person opened now, by its fixed id.
                  for (const id of ids) {
                    if (!openSections.includes(id)) {
                      track("case.sheet_section", { section: id }, { caseId: detail.id });
                    }
                  }
                  setOpenSections(ids);
                  rememberList(SHEET_SECTIONS_KEY, ids);
                }}
                onCheckAgain={onCheckAgain}
                onApprovePrice={onApprovePrice}
              />
            </Drawer.Body>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
